import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String }
  static targets = ["container"]

  connect() {
    this.setupSortable()
    this.element.addEventListener('sortable:positionsUpdated', this.handlePositionsUpdated.bind(this))
  }

  setupSortable() {
    // Store dragged element at instance level
    this.draggedElement = null
    this.draggedIndex = null

    // Add drag handlers to all items
    this.addDragHandlers()
  }

  addDragHandlers() {
    const items = this.element.querySelectorAll('.reorder-item')

    items.forEach((item, index) => {
      item.draggable = true

      const handle = item.querySelector('.fa-grip-vertical')

      if (handle) {
        handle.addEventListener('mousedown', () => {
          handle.style.cursor = 'grabbing'
        })

        handle.addEventListener('mouseup', () => {
          handle.style.cursor = 'grab'
        })
      }

      item.addEventListener('dragstart', (e) => {
        this.draggedElement = item
        this.draggedIndex = Array.from(this.element.children).indexOf(item)
        item.classList.add('dragging', 'sortable-drag')
        item.style.opacity = '0.5'
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/html', item.outerHTML)
      })

      item.addEventListener('dragend', () => {
        item.classList.remove('dragging', 'sortable-drag')
        item.style.opacity = '1'
        if (handle) handle.style.cursor = 'grab'
      })

      item.addEventListener('dragover', (e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'

        if (!this.draggedElement) return

        const afterElement = this.getDragAfterElement(e.clientY)
        if (afterElement == null) {
          this.element.appendChild(this.draggedElement)
        } else {
          this.element.insertBefore(this.draggedElement, afterElement)
        }
      })

      item.addEventListener('drop', (e) => {
        e.preventDefault()
        this.updatePositions()
      })
    })
  }

  getDragAfterElement(y) {
    const draggableElements = [...this.element.querySelectorAll('.reorder-item:not(.dragging)')]

    return draggableElements.reduce((closest, child) => {
      const box = child.getBoundingClientRect()
      const offset = y - box.top - box.height / 2

      if (offset < 0 && offset > closest.offset) {
        return { offset: offset, element: child }
      } else {
        return closest
      }
    }, { offset: Number.NEGATIVE_INFINITY }).element
  }

  updatePositions() {
    const items = this.element.querySelectorAll('.reorder-item')
    const componentIds = []

    items.forEach((item, index) => {
      const badge = item.querySelector('.badge.bg-secondary')
      if (badge) {
        badge.textContent = `Position ${index + 1}`
      }
      item.dataset.position = index
      componentIds.push(item.dataset.componentId)
    })

    // Send the reorder request to the backend
    this.sendReorderRequest(componentIds)
  }

  async sendReorderRequest(componentIds) {
    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({ template_component_ids: componentIds })
      })

      if (!response.ok) {
        throw new Error('Failed to reorder components')
      }

      // Optional: Show success feedback
      this.showNotification('Components reordered successfully', 'success')

    } catch (error) {
      console.error('Error reordering components:', error)
      this.showNotification('Failed to reorder components', 'error')

      // Refresh the page on error to restore original order
      window.location.reload()
    }
  }

  showNotification(message, type) {
    // Create a simple toast notification
    const toast = document.createElement('div')
    toast.className = `alert alert-${type === 'success' ? 'success' : 'danger'} position-fixed top-0 end-0 m-3`
    toast.style.zIndex = '9999'
    toast.textContent = message

    document.body.appendChild(toast)

    // Remove the toast after 3 seconds
    setTimeout(() => {
      toast.remove()
    }, 3000)
  }

  handlePositionsUpdated(event) {
    // This method can be used if integrating with the existing sortable controller
    console.log('Positions updated:', event.detail.positions)
  }
}